import { Delete } from '@mui/icons-material'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { range } from 'ramda'
import { useState } from 'react'
import { Field, Form, FormSpy, useForm } from 'react-final-form'
import { NumericFormat } from 'react-number-format'
import { DataPanel } from '../../../components/DataPanel'
import MultistepFormField, {
    DropdownValues,
} from '../../../components/MultistepForm/MultistepFormField'

interface SelectTeethSubFormProps {
    // formProps: FormRenderProps
    fieldName: string
}

const SelectTeethSubForm = (props: SelectTeethSubFormProps) => {
    const form = useForm()
    const renderToothNumber = (toothNumber: string | number) => {
        const subFieldName = `services-${props.fieldName}-${toothNumber}`
        return (
            <Grid item xs={1} key={toothNumber}>
                <Field name={subFieldName}>
                    {(fieldProps) => {
                        const isSelected = fieldProps.input.value ?? false

                        return (
                            <Box
                                sx={{
                                    width: '22px',
                                    height: '50px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: isSelected ? 'rgb(0, 138, 238)' : 'ButtonFace',
                                    borderRadius: '2px',
                                    cursor: 'pointer',
                                }}
                                onClick={() => {
                                    const currentValue =
                                        form.getFieldState(subFieldName)?.value ?? false
                                    form.change(subFieldName, !currentValue)
                                }}
                            >
                                <Typography>{toothNumber}</Typography>
                            </Box>
                        )
                    }}
                </Field>
            </Grid>
        )
    }

    return (
        <Grid container spacing={5}>
            <Grid item xs={7}>
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'center',
                    }}
                >
                    <Typography>Permanent</Typography>
                </Box>
                <Grid container columns={2} spacing={3}>
                    <Grid item xs={1}>
                        <Grid container columns={8} spacing={1}>
                            {range(1, 9).map(renderToothNumber)}
                            {range(0, 8).map((index: number) => {
                                const toothNumber = 32 - index
                                return renderToothNumber(toothNumber)
                            })}
                        </Grid>
                    </Grid>
                    <Grid item xs={1}>
                        <Grid container columns={8} spacing={1}>
                            {range(9, 17).map(renderToothNumber)}
                            {range(0, 8).map((index: number) => {
                                const toothNumber = 24 - index
                                return renderToothNumber(toothNumber)
                            })}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={5}>
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'center',
                    }}
                >
                    <Typography>Primary</Typography>
                </Box>
                <Grid container columns={2} spacing={3}>
                    <Grid item xs={1}>
                        <Grid container columns={5} spacing={1}>
                            {['A', 'B', 'C', 'D', 'E', 'T', 'S', 'R', 'Q', 'P'].map(
                                renderToothNumber,
                            )}
                        </Grid>
                    </Grid>
                    <Grid item xs={1}>
                        <Grid container columns={5} spacing={1}>
                            {['F', 'G', 'H', 'I', 'J', 'O', 'N', 'M', 'L', 'K'].map(
                                renderToothNumber,
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}

interface Service {
    procedureDate: string
    areaOfOralCavity: string
    toothSystem: string
    toothSurface: string
    procedureCode: string
    description: string
    fee: string
    // eslint-disable-next-line etc/no-commented-out-code
    // services-teethServiced-[1-32, A-T]
    [key: string]: boolean | string
}

const FULL_WIDTH = { width: '100%' }

const PROCEDURE_CODES: DropdownValues = [
    { key: 'D0272', value: 'D0272 - Bitewings x2' },
    { key: 'D0274', value: 'D0274 - Bitewings x4' },
    { key: 'D1206', value: 'D1206 - Fluoride' },
    { key: 'D1208', value: 'D1208 - Fluoride' },
    { key: 'D0330', value: 'D0330 - Panoramic Image' },
    { key: 'D4910', value: 'D4910 - Periodontal maintenance' },
    { key: 'D1110', value: 'D1110 - Prophylaxis – Adult' },
    { key: 'D1120', value: 'D1120 - Prophylaxis – Child' },
]

const ORAL_CAVITY_LOCATIONS: DropdownValues = [
    { key: 'none', value: 'None' },
    { key: '00', value: '00 - Entire Oral Cavity' },
    { key: '01', value: '01 - Maxilliary' },
    { key: '02', value: '02 - Mandibular' },
    { key: '10', value: '10 - Upper Right Quadrant' },
    { key: '20', value: '20 - Upper Left Quadrant' },
    { key: '30', value: '30 - Lower Right Quadrant' },
    { key: '40', value: '40 - Lower Left Quadrant' },
]

const TOOTH_SURFACES: DropdownValues = [
    { key: 'B', value: 'Buccal' },
    { key: 'D', value: 'Distal' },
    { key: 'F', value: 'Facial' },
    { key: 'I', value: 'Incisal' },
    { key: 'L', value: 'Lingual' },
    { key: 'M', value: 'Mesial' },
    { key: 'O', value: 'Occlusal' },
]

interface AddNewServiceDialogProps {
    open: boolean
    handleNewService: (newServiceValues: any) => void
    handleClose: () => void
}

const SVC_DIALOG_FIELDS = [
    'procedureDate',
    'oralCavity',
    'toothSystem',
    'toothSurface',
    'procedureCode',
    'description',
    'fee',
]

// Weird form side effect w/ description when using component state...pulling this out to here for now
let lastProcedureCode: string

const AddNewServiceDialog = (props: AddNewServiceDialogProps) => {
    const renderField = (content: JSX.Element) => (
        <Grid item xs={6}>
            <Box sx={{ width: '75%', marginLeft: 'auto', marginRight: 'auto' }}>{content}</Box>
        </Grid>
    )

    return (
        <Form
            onSubmit={(formState, form) => {
                props.handleNewService(formState)
                form.reset()
            }}
            validate={(values) => {
                const errors: any = {}
                SVC_DIALOG_FIELDS.map((fieldName) => {
                    if (
                        !values[fieldName] ||
                        values[fieldName] === '' ||
                        values[fieldName].length === 0
                    ) {
                        errors[fieldName] = 'Required'
                    }
                })
                return errors
            }}
            initialValues={{ toothSystem: 'JP', oralCavity: 'none', toothSurface: [] }}
        >
            {(formProps) => (
                <Dialog open={props.open} maxWidth="xl">
                    <FormSpy
                        subscription={{ values: true }}
                        onChange={(formState) => {
                            const procedureCode = formState.values['procedureCode']
                            const procedureDescription = PROCEDURE_CODES.find(
                                ({ key }) => key === procedureCode,
                            )?.value.substring(8)
                            if (procedureDescription && lastProcedureCode !== procedureCode) {
                                formProps.form.change('description', procedureDescription)
                            }
                            lastProcedureCode = procedureCode
                        }}
                    />
                    <DialogTitle>Add New Service</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2}>
                            {renderField(
                                <>
                                    <Typography>Procedure Date</Typography>
                                    <MultistepFormField
                                        name="procedureDate"
                                        type="date"
                                        inputProps={{
                                            disableFuture: true,
                                            minDate: dayjs().subtract(1, 'year'),
                                            sx: FULL_WIDTH,
                                        }}
                                    />
                                </>,
                            )}
                            {renderField(
                                <>
                                    <Typography>Area of Oral Cavity</Typography>
                                    <MultistepFormField
                                        name="oralCavity"
                                        type="dropdown"
                                        dropdownValues={ORAL_CAVITY_LOCATIONS}
                                        inputProps={{ sx: { ...FULL_WIDTH, height: '40px' } }}
                                    />
                                </>,
                            )}
                            {renderField(
                                <>
                                    <Typography>Tooth System</Typography>
                                    <MultistepFormField
                                        name="toothSystem"
                                        inputProps={{ disabled: true, sx: FULL_WIDTH }}
                                    />
                                </>,
                            )}
                            {renderField(
                                <>
                                    <Typography>Tooth Surface</Typography>
                                    <MultistepFormField
                                        name="toothSurface"
                                        type="dropdown"
                                        dropdownValues={TOOTH_SURFACES}
                                        inputProps={{
                                            multiple: true,
                                            sx: { ...FULL_WIDTH, height: '40px' },
                                        }}
                                    />
                                </>,
                            )}
                            {renderField(
                                <>
                                    <Typography>Procedure Code</Typography>
                                    <MultistepFormField
                                        name="procedureCode"
                                        type="dropdown"
                                        dropdownValues={PROCEDURE_CODES}
                                        inputProps={{ sx: { ...FULL_WIDTH, height: '40px' } }}
                                    />
                                </>,
                            )}
                            {renderField(
                                <>
                                    <Typography>Description</Typography>
                                    <MultistepFormField
                                        name="description"
                                        inputProps={{ sx: FULL_WIDTH }}
                                    />
                                </>,
                            )}
                            {renderField(
                                <>
                                    <Typography>Fee</Typography>
                                    <MultistepFormField
                                        name="fee"
                                        type="currency"
                                        inputProps={{
                                            allowNegative: false,
                                            sx: FULL_WIDTH,
                                        }}
                                    />
                                </>,
                            )}

                            <Grid item xs={12}>
                                <SelectTeethSubForm fieldName="teethServiced" />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={props.handleClose}>Cancel</Button>
                        <Button onClick={formProps.handleSubmit} disabled={formProps.invalid}>
                            Add Service
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Form>
    )
}

const ServicesStep = () => {
    const form = useForm()
    const [serviceDialogOpen, setServiceDialogOpen] = useState(false)
    const [internalSvcFormState, setInternalSvcFormState] = useState<Service[]>([])

    const onFormChange = () => {
        setInternalSvcFormState(form.getState().values['services-servicesProvided'] ?? [])
    }

    const onNewService = (service: Service) => {
        setServiceDialogOpen(false)
        const existingServices = form.getState().values['services-servicesProvided'] ?? []
        form.change('services-servicesProvided', [...existingServices, service])
    }

    const renderServices = () => {
        const services: Service[] = internalSvcFormState

        return services.map((service: any, i: number) => {
            const touchedTeeth = Object.keys(service)
                .filter((key) => key.includes('services-teethServiced') && service[key])
                .sort((a, b) => {
                    const toothIndexA = a.split('-')[2]
                    const toothIndexB = b.split('-')[2]

                    const toothIntA = parseInt(toothIndexA)
                    const toothIntB = parseInt(toothIndexB)
                    const intANaN = Number.isNaN(toothIntA)
                    const intBNaN = Number.isNaN(toothIntB)

                    if (intANaN && intBNaN) {
                        return toothIndexA.localeCompare(toothIndexB)
                    }

                    if (!intANaN && !intBNaN) {
                        return toothIntA - toothIntB
                    }

                    return intANaN ? 1 : -1
                })

            // TODO: this is gross
            const teethRanged = touchedTeeth.reduce(
                (acc: any, toothKey: any) => {
                    const { userFriendlyExp, currentRange } = acc
                    const toothIndex = toothKey.split('-')[2]
                    const toothIntIndex = parseInt(toothIndex)

                    const buildExpression = (appendToRange: boolean) => {
                        if (appendToRange) {
                            return {
                                userFriendlyExp,
                                currentRange: [...currentRange, toothIndex],
                            }
                        }
                        if (currentRange.length > 1) {
                            return {
                                userFriendlyExp: userFriendlyExp.length
                                    ? `${userFriendlyExp}, ${currentRange[0]}-${
                                          currentRange[currentRange.length - 1]
                                      }`
                                    : `${currentRange[0]}-${currentRange[currentRange.length - 1]}`,
                                currentRange: [toothIndex],
                            }
                        } else {
                            if (currentRange[0]) {
                                return {
                                    userFriendlyExp: userFriendlyExp.length
                                        ? `${userFriendlyExp}, ${currentRange[0]}`
                                        : `${currentRange[0]}`,
                                    currentRange: [toothIndex],
                                }
                            } else {
                                return {
                                    userFriendlyExp,
                                    currentRange: [toothIndex],
                                }
                            }
                        }
                    }

                    if (Number.isNaN(toothIntIndex)) {
                        const lastCode = currentRange[currentRange.length - 1]
                        if (!Number.isNaN(parseInt(lastCode))) return buildExpression(false)
                        const charCode = toothIndex?.charCodeAt(0)

                        return buildExpression(
                            lastCode === undefined || charCode - lastCode?.charCodeAt(0) === 1,
                        )
                    }

                    const lastInt = currentRange[currentRange.length - 1]
                    if (Number.isNaN(lastInt)) {
                        return buildExpression(false)
                    }

                    return buildExpression(toothIntIndex - lastInt === 1)
                },
                { userFriendlyExp: '', currentRange: [] },
            )

            const formattedTeethFinalRange =
                teethRanged.currentRange.length > 1
                    ? `${teethRanged.currentRange[0]}-${
                          teethRanged.currentRange[teethRanged.currentRange.length - 1]
                      }`
                    : teethRanged.currentRange[0]
            const formattedTeeth = teethRanged.userFriendlyExp.length
                ? `${teethRanged.userFriendlyExp}, ${formattedTeethFinalRange}`
                : formattedTeethFinalRange

            return (
                <TableRow key={i}>
                    <TableCell>
                        <Typography>{dayjs(service.procedureDate).format('MM/DD/YYYY')}</Typography>
                    </TableCell>
                    <TableCell>
                        <Typography>
                            {service.oralCavity === 'none' ? 'None' : service.oralCavity}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography>{service.toothSystem}</Typography>
                    </TableCell>
                    <TableCell>
                        <Typography>{formattedTeeth}</Typography>
                    </TableCell>
                    <TableCell>
                        <Typography>{service.toothSurface}</Typography>
                    </TableCell>
                    <TableCell>
                        <Typography>{service.procedureCode}</Typography>
                    </TableCell>
                    <TableCell>
                        <Typography>{service.description}</Typography>
                    </TableCell>
                    <TableCell>
                        <NumericFormat
                            value={service.fee}
                            thousandSeparator
                            prefix="$"
                            displayType="text"
                            renderText={(value) => <Typography>{value}</Typography>}
                        />
                    </TableCell>
                    <TableCell>
                        <IconButton
                            onClick={() => {
                                let newServices = [...services]
                                for (let j = i; j < newServices.length; j++) {
                                    newServices[j] = newServices[j + 1]
                                }
                                newServices = newServices.slice(0, newServices.length - 1)
                                form.change('services-servicesProvided', newServices)
                                setInternalSvcFormState(newServices)
                            }}
                        >
                            <Delete />
                        </IconButton>
                    </TableCell>
                </TableRow>
            )
        })
    }

    return (
        <Box>
            <FormSpy subscription={{ values: true }} onChange={onFormChange} />
            <AddNewServiceDialog
                open={serviceDialogOpen}
                handleClose={() => setServiceDialogOpen(false)}
                handleNewService={onNewService}
            />
            <Field name="services-servicesProvided" render={() => <></>} />
            <Stack spacing={2}>
                <DataPanel
                    title="Record of Services Provided"
                    widget={<Button onClick={() => setServiceDialogOpen(true)}>Add New Row</Button>}
                    content={
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Procedure Date (MM/DD/CCYY)</TableCell>
                                        <TableCell>Area of Oral Cavity</TableCell>
                                        <TableCell>Tooth System</TableCell>
                                        <TableCell>Tooth Number(s) or Letter(s)</TableCell>
                                        <TableCell>Tooth Surface</TableCell>
                                        <TableCell>Procedure Code</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Fee</TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>{renderServices()}</TableBody>
                            </Table>
                        </TableContainer>
                    }
                />
                <DataPanel
                    title="Missing Teeth Information"
                    content={
                        <Box>
                            <Typography>Select each missing tooth</Typography>
                            <SelectTeethSubForm fieldName="missingTeeth" />
                        </Box>
                    }
                />
            </Stack>
        </Box>
    )
}

export default ServicesStep
